import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const CATEGORIES = ['Demo & Site Prep', 'Xeriscape', 'Sod', 'Pavers', 'Planter', 'Irrigation', 'Tree Service', 'Labor', 'General']
const UNITS = ['sq ft', 'sq yd', 'lin ft', 'ton', 'yard', 'bag', 'ea', 'pallet', 'sum', 'hr', 'gal', 'lb']

export default function MaterialPriceList() {
  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [filterCat, setFilterCat] = useState('All')
  const [form, setForm] = useState({ name: '', category: 'General', unit: 'ea', unit_cost: '', notes: '' })

  useEffect(() => { fetchMaterials() }, [])

  async function fetchMaterials() {
    setLoading(true)
    const { data, error } = await supabase.from('materials').select('*').order('category').order('name')
    if (error) setError('Failed to load materials: ' + error.message)
    else setMaterials(data || [])
    setLoading(false)
  }

  function set(field, value) { setForm(f => ({ ...f, [field]: value })) }

  function startEdit(mat) {
    setForm({ name: mat.name, category: mat.category, unit: mat.unit, unit_cost: mat.unit_cost, notes: mat.notes || '' })
    setEditingId(mat.id)
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function resetForm() {
    setForm({ name: '', category: 'General', unit: 'ea', unit_cost: '', notes: '' })
    setEditingId(null)
    setShowForm(false)
    setError(null)
  }

  async function saveMaterial() {
    if (!form.name.trim()) return setError('Material name is required.')
    if (!form.unit_cost || isNaN(parseFloat(form.unit_cost))) return setError('Unit cost must be a valid number.')
    setSaving(true)
    setError(null)
    const payload = {
      name: form.name.trim(),
      category: form.category,
      unit: form.unit,
      unit_cost: parseFloat(form.unit_cost),
      notes: form.notes.trim() || null
    }
    let err
    if (editingId) {
      const res = await supabase.from('materials').update(payload).eq('id', editingId)
      err = res.error
    } else {
      const res = await supabase.from('materials').insert([payload])
      err = res.error
    }
    if (err) { setError('Save failed: ' + err.message); setSaving(false); return }
    setSaving(false)
    resetForm()
    fetchMaterials()
  }

  async function deleteMaterial(id, name) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return
    const { error } = await supabase.from('materials').delete().eq('id', id)
    if (error) setError('Delete failed: ' + error.message)
    else setMaterials(prev => prev.filter(m => m.id !== id))
  }

  const cats = ['All', ...CATEGORIES]
  const filtered = materials.filter(m => {
    const matchSearch = m.name.toLowerCase().includes(search.toLowerCase())
    const matchCat = filterCat === 'All' || m.category === filterCat
    return matchSearch && matchCat
  })

  // Group by category for display
  const grouped = {}
  for (const m of filtered) {
    if (!grouped[m.category]) grouped[m.category] = []
    grouped[m.category].push(m)
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Material Price List</h1>
          <p style={s.sub}>{materials.length} materials · Used in estimate builder</p>
        </div>
        <button style={s.addBtn} onClick={() => { resetForm(); setShowForm(true) }}>+ Add Material</button>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {showForm && (
        <div style={s.formCard}>
          <div style={s.formHeader}>
            <h2 style={s.formTitle}>{editingId ? 'Edit Material' : 'New Material'}</h2>
            <button style={s.closeBtn} onClick={resetForm}>✕</button>
          </div>
          <div style={s.formGrid}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={s.label}>Material Name</label>
              <input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. TifTuf Bermuda Sod" autoFocus />
            </div>
            <div>
              <label style={s.label}>Category</label>
              <select style={s.input} value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Unit</label>
              <select style={s.input} value={form.unit} onChange={e => set('unit', e.target.value)}>
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Unit Cost ($)</label>
              <input style={s.input} type="number" step="0.01" min="0" value={form.unit_cost} onChange={e => set('unit_cost', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label style={s.label}>Notes (optional)</label>
              <input style={s.input} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="e.g. Confirm price at nursery" />
            </div>
          </div>
          <div style={s.formActions}>
            <button style={s.cancelBtn} onClick={resetForm}>Cancel</button>
            <button style={s.saveBtn} onClick={saveMaterial} disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update Material' : 'Add Material'}</button>
          </div>
        </div>
      )}

      <div style={s.filters}>
        <input style={s.search} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search materials..." />
        <div style={s.catTabs}>
          {cats.map(c => (
            <button key={c} style={{ ...s.catTab, ...(filterCat === c ? s.catTabActive : {}) }} onClick={() => setFilterCat(c)}>{c}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>Loading materials...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>{materials.length === 0 ? 'No materials yet. Add your first material above.' : 'No materials match your search.'}</div>
      ) : (
        Object.entries(grouped).map(([cat, mats]) => (
          <div key={cat} style={s.group}>
            <div style={s.groupHeader}>{cat} <span style={s.groupCount}>{mats.length}</span></div>
            <div style={s.table}>
              <div style={s.tableHeader}>
                <span style={{ flex: 3 }}>Material</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Unit</span>
                <span style={{ flex: 1, textAlign: 'right' }}>Unit Cost</span>
                <span style={{ flex: 2, textAlign: 'left', paddingLeft: '16px' }}>Notes</span>
                <span style={{ flex: 1, textAlign: 'right' }}>Actions</span>
              </div>
              {mats.map(m => (
                <div key={m.id} style={s.tableRow}>
                  <span style={{ flex: 3, fontWeight: '500', color: '#1a2e1a' }}>{m.name}</span>
                  <span style={{ flex: 1, textAlign: 'center', color: '#6b7c6b', fontSize: '13px' }}>{m.unit}</span>
                  <span style={{ flex: 1, textAlign: 'right', fontWeight: '600', color: '#2d4a2d' }}>${parseFloat(m.unit_cost).toFixed(2)}</span>
                  <span style={{ flex: 2, textAlign: 'left', paddingLeft: '16px', color: '#9ca3af', fontSize: '12px', fontStyle: 'italic' }}>{m.notes || '—'}</span>
                  <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                    <button style={s.editBtn} onClick={() => startEdit(m)}>Edit</button>
                    <button style={s.deleteBtn} onClick={() => deleteMaterial(m.id, m.name)}>✕</button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

const s = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  addBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '14px', marginBottom: '16px' },
  formCard: { background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid #2d4a2d' },
  formHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  formTitle: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7c6b' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  formActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn: { background: 'none', border: '1.5px solid #d1d5db', color: '#374151', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  saveBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  filters: { marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' },
  search: { padding: '9px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', width: '300px', boxSizing: 'border-box' },
  catTabs: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  catTab: { padding: '5px 12px', borderRadius: '20px', border: '1.5px solid #e5e7eb', background: 'white', color: '#6b7c6b', fontSize: '12px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  catTabActive: { background: '#2d4a2d', color: 'white', border: '1.5px solid #2d4a2d' },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  empty: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  group: { marginBottom: '24px' },
  groupHeader: { fontSize: '13px', fontWeight: '700', color: '#2d4a2d', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' },
  groupCount: { background: '#f0fdf4', color: '#16a34a', borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: '600' },
  table: { background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' },
  tableHeader: { display: 'flex', padding: '10px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
  tableRow: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '14px' },
  editBtn: { background: 'none', border: '1px solid #d1d5db', color: '#374151', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' },
  deleteBtn: { background: 'none', border: '1px solid #fecaca', color: '#dc2626', cursor: 'pointer', fontSize: '13px', padding: '4px 8px', borderRadius: '6px', fontFamily: 'inherit' },
}
